/**
 * What is actually deployed right now.
 *
 * This exists because of a specific requirement on RUS-5: we have to *prove* a rollback
 * worked, not assume it did. A page that says "Hello world" looks identical before and
 * after a rollback. A page that says which commit built it does not. So the deployed
 * commit is rendered in the UI from the first deploy onward, and the smoke test in CI
 * asserts against it.
 *
 * Framework-free on purpose (ADR §3.1): this is plain TypeScript with no React and no DOM,
 * so it is testable under plain Node and survives a move to React Native unchanged.
 */

/** The provenance of the bundle currently being served. */
export interface BuildInfo {
  /** Full git SHA the bundle was built from, or `unknown` outside CI. */
  commit: string;
  /** First 7 characters of `commit` — what a human reads and compares. */
  shortCommit: string;
  /** ISO-8601 build timestamp, or `unknown` outside CI. */
  builtAt: string;
  /** Deploy target this bundle was built for. */
  environment: string;
  /** True when the values came from a real CI build rather than dev fallbacks. */
  isRelease: boolean;
}

/** The literal we substitute when a build variable was never provided. */
export const UNKNOWN = 'unknown';

/** Length of the abbreviated SHA. Matches `git rev-parse --short` default. */
const SHORT_SHA_LENGTH = 7;

/**
 * The subset of build-time environment we read. Vite exposes `VITE_`-prefixed variables
 * on `import.meta.env`; every value arrives as a string or is absent entirely.
 */
export interface BuildEnv {
  VITE_COMMIT_SHA?: string | undefined;
  VITE_BUILT_AT?: string | undefined;
  VITE_ENVIRONMENT?: string | undefined;
}

/**
 * Trim a build variable down to a usable value, treating blank and literal-`undefined`
 * strings as absent. CI substitution failures characteristically produce exactly those,
 * and silently rendering the string "undefined" as a commit SHA would be worse than
 * plainly admitting we do not know.
 */
function clean(raw: string | undefined): string | undefined {
  if (raw === undefined) return undefined;
  const trimmed = raw.trim();
  if (trimmed === '' || trimmed === 'undefined' || trimmed === 'null') return undefined;
  return trimmed;
}

/**
 * Derive the deployed-build banner from build-time environment variables.
 *
 * Never throws. A malformed or missing variable degrades to `unknown` rather than
 * breaking the page: a field app that refuses to render because a build label is missing
 * is a worse failure than one that renders honestly without it.
 */
export function parseBuildInfo(env: BuildEnv): BuildInfo {
  const commit = clean(env.VITE_COMMIT_SHA);
  const builtAt = clean(env.VITE_BUILT_AT);
  const environment = clean(env.VITE_ENVIRONMENT);

  return {
    commit: commit ?? UNKNOWN,
    shortCommit: commit === undefined ? UNKNOWN : commit.slice(0, SHORT_SHA_LENGTH),
    builtAt: builtAt ?? UNKNOWN,
    environment: environment ?? 'development',
    // A release is a build where CI told us the commit. Everything else is someone's
    // laptop, and the UI should not imply otherwise.
    isRelease: commit !== undefined,
  };
}

/**
 * Format the build for display. Kept next to the parser so the deployed string and the
 * string CI smoke-tests against cannot drift apart.
 */
export function formatBuildLabel(info: BuildInfo): string {
  return info.isRelease
    ? `${info.environment} · ${info.shortCommit}`
    : `${info.environment} · not a release build`;
}
